import { useTranslation } from "react-i18next"

import { ShieldCheck, Sparkles } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"

const FRAMEWORK_ENTRIES: readonly {
  id: string
  descKey: "abk09" | "ab04" | "abt06" | "amaaf12" | "lou" | "gdpr"
  labelKey?: "gdpr"
}[] = [
  { id: "ABK 09", descKey: "abk09" },
  { id: "AB 04", descKey: "ab04" },
  { id: "ABT 06", descKey: "abt06" },
  { id: "AMA AF 12", descKey: "amaaf12" },
  { id: "LOU", descKey: "lou" },
  { id: "GDPR", descKey: "gdpr", labelKey: "gdpr" },
]

type BidReviewFrameworkSelectorProps = {
  autoDetectedFrameworks: string[]
  selectedFrameworks: string[]
  onSelectionChange: (frameworks: string[]) => void
}

export function BidReviewFrameworkSelector({
  autoDetectedFrameworks,
  selectedFrameworks,
  onSelectionChange,
}: BidReviewFrameworkSelectorProps) {
  const { t } = useTranslation()
  const hasNoAutoDetected = autoDetectedFrameworks.length === 0
  const hasNoSelection = selectedFrameworks.length === 0

  function handleToggle(frameworkId: string, checked: boolean) {
    if (checked) {
      onSelectionChange([...selectedFrameworks, frameworkId])
    } else {
      onSelectionChange(selectedFrameworks.filter((id) => id !== frameworkId))
    }
  }

  function frameworkLabel(fw: (typeof FRAMEWORK_ENTRIES)[number]): string {
    return fw.labelKey ? t(`tender.bidReview.frameworkSelector.labels.${fw.labelKey}`) : fw.id
  }

  return (
    <div className="bg-muted/30 rounded-lg border p-4">
      <div className="mb-3 flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-muted-foreground size-4 shrink-0" />
          <h4 className="text-[13px] font-semibold">
            {t("tender.bidReview.frameworkSelector.title")}
          </h4>
        </div>
        <p className="text-muted-foreground pl-6 text-[12px]">
          {t("tender.bidReview.frameworkSelector.description")}
        </p>
      </div>

      <div className="flex flex-col gap-2 pl-1">
        {FRAMEWORK_ENTRIES.map((fw) => {
          const isAutoDetected = autoDetectedFrameworks.includes(fw.id)
          const isChecked = selectedFrameworks.includes(fw.id)

          return (
            <label
              key={fw.id}
              htmlFor={`fw-${fw.id}`}
              className={cn(
                "flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-1.5 transition-colors",
                isChecked ? "bg-background" : "hover:bg-muted/50"
              )}
            >
              <Checkbox
                checked={isChecked}
                className="mt-0.5 shrink-0"
                id={`fw-${fw.id}`}
                onCheckedChange={(checked) => handleToggle(fw.id, checked === true)}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[13px] font-medium">{frameworkLabel(fw)}</span>
                  {isAutoDetected && (
                    <Badge
                      className="text-muted-foreground h-4 gap-0.5 border-dashed px-1.5 text-[10px] font-normal"
                      variant="outline"
                    >
                      <Sparkles className="size-2.5" />
                      {t("tender.bidReview.frameworkSelector.autoDetectedBadge")}
                    </Badge>
                  )}
                </div>
                <p className="text-muted-foreground/70 text-[11px] leading-relaxed">
                  {t(`tender.bidReview.frameworkSelector.descriptions.${fw.descKey}`)}
                </p>
              </div>
            </label>
          )
        })}
      </div>

      {hasNoAutoDetected && hasNoSelection && (
        <p className="text-muted-foreground/60 mt-2 pl-1 text-[11px] italic">
          {t("tender.bidReview.frameworkSelector.noneAutoDetected")}
        </p>
      )}
    </div>
  )
}
